import { useEffect, useState } from "react";

/**
 * 900 px — the app's one idea of "a phone".
 *
 * It is the width `DashboardLayout` uses to decide the navigation collapses,
 * and the width at which Inventory's table becomes cards (Deliveries is cards
 * at every width since 2026-10-03, so it no longer asks):
 * there, the narrow screen and the collapsed nav are the same event. That file
 * keeps its own listener because it does more than watch the width (it closes
 * the open nav), but the number must stay the same in both places: tokens.css
 * documents 900 px as load-bearing, and `useCardLayout.spec.ts` now reads
 * `DashboardLayout.tsx` and fails if the two drift apart.
 */
export const CARD_LAYOUT_MQ = "(max-width: 900px)";

/*
 * G-145: the 1000 px "tablet cards" width (G-106) is gone with the Team table
 * it existed for — the Team page is tiles at every width. A page that needs a
 * second width again adds it here, to the union below, rather than inline.
 */
export type CardLayoutMq = typeof CARD_LAYOUT_MQ;

/**
 * True while the viewport is at or below the width passed in. The width can
 * change without a reload — a rotation, a resized desktop window — hence the
 * listener rather than a one-time read.
 *
 * G-92 wrote this loop inside `InventoryPanel` and G-95 needed the identical
 * one for the deliveries list (since retired there), so it lives here and every page reads one
 * definition rather than several that can drift apart.
 *
 * The query is a required argument, with no default: one number quietly
 * serving two purposes is exactly what G-106 had to take apart, so which width
 * a page is on is stated at the page, not hidden in a signature.
 */
export function useCardLayout(mq: CardLayoutMq): boolean {
  const [cardLayout, setCardLayout] = useState(
    () => typeof window !== "undefined" && window.matchMedia(mq).matches,
  );

  useEffect(() => {
    const media = window.matchMedia(mq);
    const apply = () => setCardLayout(media.matches);
    // Not redundant: this is what re-syncs state when `mq` itself changes, and
    // what covers a width that moved between the first render and this effect.
    apply();
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [mq]);

  return cardLayout;
}
